/**
 * Counters on the menu (T11-21): how many sold-twice cards wait for a decision — shown on the
 * Orders group and on the "problems to check" entry, so a conflict is seen from any screen. Read
 * from inventory-service (`/admin/inventory/conflicts?filter[status]=open`) every minute and
 * whenever a stock screen refreshes the inventory data (same query-key prefix).
 * T11-31: new "consign with us" requests (`/admin/consignment-requests?status=new`) on the
 * Consignment group and its Requests entry.
 * T11-52: both count themselves — a conflict (`staff:inventory` / `staff:orders`) or a request
 * (`staff:consignment`) refetches its counter at once; the 60 s poll is only the fallback.
 */
import { useQuery } from "@tanstack/react-query";
import { createConsignmentClient, createInventoryClient } from "@tcg/api-client";
import { createContext, type ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";

import { unwrap } from "@/lib/api/errors";
import { useServiceClient } from "@/lib/api/use-api";
import { usePermissions } from "@/lib/auth/usePermissions";
import { useLiveInvalidate } from "@/realtime/hooks";

export type BadgeTarget = `group:${string}` | `item:${string}`;
export type NavBadgeCounts = Partial<Record<BadgeTarget, number>>;

const NavBadgeContext = createContext<NavBadgeCounts>({});

export const NAV_BADGE_KEY = ["inventory", "conflicts", "nav-badge"] as const;
/** New "consign with us" requests (T11-31) — same prefix as the requests screen. */
export const REQUESTS_BADGE_KEY = ["consignment", "requests", "nav-badge"] as const;

/** What the counter of a target means (screen readers). */
const BADGE_LABEL: Partial<Record<BadgeTarget, string>> = {
  "group:consignment": "nav.badge_requests",
  "item:consignment_requests": "nav.badge_requests",
};

export function NavBadgesProvider({ children }: { children: ReactNode }) {
  const { can } = usePermissions();
  const inventory = useServiceClient(createInventoryClient);
  const enabled = can("inventory.read") && can("inventory.conflicts.resolve");
  const conflictsPoll = useLiveInvalidate("staff:inventory", [NAV_BADGE_KEY], {
    match: (m) => m.entity === "oversell_conflict",
    pollMs: 60_000,
  });
  const open = useQuery({
    queryKey: NAV_BADGE_KEY,
    enabled,
    staleTime: 20_000,
    refetchInterval: conflictsPoll,
    refetchIntervalInBackground: false,
    meta: { errorToast: false, silent: true },
    queryFn: async ({ signal }) => {
      const page = await unwrap(
        inventory.GET("/v1/admin/inventory/conflicts", {
          params: { query: { "filter[status]": "open", limit: 100 } },
          signal,
        }),
      );
      return page.pageInfo.totalCount ?? page.data.length;
    },
  });
  const consignment = useServiceClient(createConsignmentClient);
  const requestsEnabled = can("consignment.read") && can("consignment.manage");
  const requestsPoll = useLiveInvalidate("staff:consignment", [REQUESTS_BADGE_KEY], {
    match: (m) => m.entity === "consignment_request",
    pollMs: 60_000,
  });
  const requests = useQuery({
    queryKey: REQUESTS_BADGE_KEY,
    enabled: requestsEnabled,
    staleTime: 20_000,
    refetchInterval: requestsPoll,
    refetchIntervalInBackground: false,
    meta: { errorToast: false, silent: true },
    queryFn: async ({ signal }) => {
      const page = await unwrap(
        consignment.GET("/v1/admin/consignment-requests", {
          params: { query: { status: "new", limit: 100 } },
          signal,
        }),
      );
      return page.data.length;
    },
  });
  const count = enabled ? (open.data ?? 0) : 0;
  const newRequests = requestsEnabled ? (requests.data ?? 0) : 0;
  const value: NavBadgeCounts = {
    ...(count
      ? { "group:orders": count, "group:inventory": count, "item:inventory_conflicts": count }
      : {}),
    ...(newRequests
      ? { "group:consignment": newRequests, "item:consignment_requests": newRequests }
      : {}),
  };
  return <NavBadgeContext.Provider value={value}>{children}</NavBadgeContext.Provider>;
}

export function useNavBadge(target: BadgeTarget): number {
  return useContext(NavBadgeContext)[target] ?? 0;
}

/** A small red counter; screen readers hear "3 problems to check". */
export function NavBadge({ target, className }: { target: BadgeTarget; className?: string }) {
  const { t } = useTranslation();
  const count = useNavBadge(target);
  if (!count) {
    return null;
  }
  return (
    <span
      data-slot="nav-badge"
      data-target={target}
      className={
        className ??
        "ml-auto inline-flex min-w-5 shrink-0 items-center justify-center rounded-full bg-[#b91c1c] px-1.5 text-xs leading-5 font-bold text-white"
      }
    >
      {count > 99 ? "99+" : count}
      <span className="sr-only"> {t(BADGE_LABEL[target] ?? "nav.badge_conflicts", { count })}</span>
    </span>
  );
}
